/**
 * Servicio de Keycloak para manejo de autenticación
 * Versión optimizada y limpia
 */
import Keycloak from 'keycloak-js';
import { getKeycloakConfig, keycloakInitOptions, keycloakTimeouts, roleMapping, getCurrentEnvironment, loggingConfig } from '../config/keycloak.config';
class KeycloakService {
    constructor() {
        this.keycloak = null;
        this.refreshTimer = null;
        this.config = getKeycloakConfig();
        this.environment = getCurrentEnvironment();
        this.logging = loggingConfig[this.environment];
        this.initialized = false;
    }
    /**
     * Inicializa Keycloak
     */
    async initialize() {
        if (this.initialized && this.keycloak) {
            return this.keycloak.authenticated ?? false;
        }
        this.keycloak = new Keycloak({
            url: this.config.url,
            realm: this.config.realm,
            clientId: this.config.clientId,
        });
        try {
            // console.log('BEFORE INIT URL', window.location.href);
            const authenticated = await this.keycloak.init(keycloakInitOptions);
            // console.log('AFTER INIT URL', window.location.href);
            // console.log("INIT RESULT", authenticated);
            // console.log("KC AUTH", this.keycloak.authenticated);
            // console.log("KC TOKEN", this.keycloak.token);
            console.log("KC TOKEN PARSED", this.keycloak.tokenParsed);
            // console.log("URL", window.location.href);
            this.initialized = true;
            if (authenticated && this.keycloak.token) {
                this.setupTokenRefresh();
                this.setupEventListeners();
                this._tenant = this.keycloak?.tokenParsed?.group?.[0]?.replace("/", "") || "";
            }
            return this.keycloak.authenticated === true && !!this.keycloak.token;
        }
        catch (error) {
            console.error('error', 'Error inicializando Keycloak', error);
            return false;
        }
    }
    /**
     * Realiza login redirigiendo a Keycloak
     */
    async login() {
        if (!this.keycloak) {
            await this.initialize();
        }
        await this.keycloak.login({
            redirectUri: this.config.redirectUri,
        });
    }
    /**
     * Realiza logout
     */
    async logout() {
        if (!this.keycloak) {
            return;
        }
        try {
            this.log('info', '🔐 Cerrando sesión...');
            this.clearRefreshTimer();
            await this.keycloak.logout({
                redirectUri: this.config.postLogoutRedirectUri,
            });
        }
        catch (error) {
            this.log('error', '❌ Error en logout:', error);
            throw error;
        }
    }
    getTenant() {
        return this._tenant || undefined;
    }
    getRoles(tokenParsed) {
        // Extraer roles del token
        const realmRoles = tokenParsed?.realm_access?.roles || [];
        const clientRoles = tokenParsed?.resource_access?.[this.config.clientId]?.roles || [];
        return [...realmRoles, ...clientRoles];
    }
    /**
     * Obtiene información del usuario autenticado
     */
    async getUserInfo() {
        if (!this.keycloak) {
            this.log('error', '❌ Keycloak no está inicializado');
            return null;
        }
        if (!this.keycloak.authenticated || !this.keycloak.token) {
            return null;
        }
        try {
            const userInfo = await this.keycloak.loadUserInfo();
            const tokenParsed = this.keycloak.tokenParsed;
            if (!userInfo && !tokenParsed) {
                this.log('error', '❌ No se pudo obtener información del usuario');
                return null;
            }
            // Cambiar al terminar implementacion
            const partyInfo = {
                partyCode: userInfo?.["party-code"],
                idType: userInfo?.["identification-type"],
                idValue: userInfo?.["identification-value"],
            };
            localStorage.setItem("party-info", JSON.stringify(partyInfo));
            localStorage.setItem("user-party-code", userInfo?.["party-code"]);
            // Extraer roles del token
            const allRoles = this.getRoles(tokenParsed);
            // Mapear roles a permisos
            const permissions = this.mapRolesToPermissions(allRoles);
            // Crear información final del usuario
            const finalUserInfo = {
                id: userInfo?.sub || tokenParsed?.sub || '',
                username: userInfo?.preferred_username || tokenParsed?.preferred_username || 'usuario',
                email: userInfo?.email || tokenParsed?.email || '',
                firstName: userInfo?.given_name || tokenParsed?.given_name || '',
                lastName: userInfo?.family_name || tokenParsed?.family_name || '',
                name: userInfo?.name || tokenParsed?.name || userInfo?.preferred_username || tokenParsed?.preferred_username || 'Usuario',
                roles: allRoles,
                permissions,
                realm: this.config.realm,
            };
            return finalUserInfo;
        }
        catch (error) {
            this.log('error', '❌ Error obteniendo información del usuario:', error);
            // Fallback: usar solo el token parseado
            try {
                const tokenParsed = this.keycloak.tokenParsed;
                if (tokenParsed && tokenParsed.sub) {
                    this.log('warn', '⚠️ Usando fallback con token parseado');
                    const allRoles = this.getRoles(tokenParsed);
                    const permissions = this.mapRolesToPermissions(allRoles);
                    const fallbackUserInfo = {
                        id: tokenParsed.sub,
                        username: tokenParsed.preferred_username || 'usuario',
                        email: tokenParsed.email || '',
                        firstName: tokenParsed.given_name || '',
                        lastName: tokenParsed.family_name || '',
                        name: tokenParsed.name || tokenParsed.preferred_username || 'Usuario',
                        roles: allRoles,
                        permissions,
                        realm: this.config.realm,
                    };
                    return fallbackUserInfo;
                }
            }
            catch (fallbackError) {
                this.log('error', '❌ Error en fallback:', fallbackError);
            }
            return null;
        }
    }
    /**
     * Obtiene el token de acceso actual
     */
    getToken() {
        return this.keycloak?.token || null;
    }
    /**
     * Obtiene el refresh token actual
     */
    getRefreshToken() {
        return this.keycloak?.refreshToken || null;
    }
    /**
     * Verifica si el usuario está autenticado
     */
    isAuthenticated() {
        return this.keycloak?.authenticated || false;
    }
    /**
     * Verifica si el token es válido
     */
    isTokenValid() {
        if (!this.keycloak || !this.keycloak.authenticated) {
            return false;
        }
        return !this.keycloak.isTokenExpired();
    }
    /**
     * Actualiza el token de acceso
     */
    async refreshToken() {
        if (!this.keycloak) {
            return false;
        }
        try {
            const refreshed = await this.keycloak.updateToken(keycloakTimeouts.minValiditySeconds);
            this.setupTokenRefresh();
            return refreshed;
        }
        catch (error) {
            this.log('error', '❌ Error actualizando token:', error);
            return false;
        }
    }
    /**
     * Verifica si el usuario tiene un permiso específico
     */
    hasPermission(permission) {
        if (!this.keycloak || !this.keycloak.authenticated) {
            return false;
        }
        const tokenParsed = this.keycloak.tokenParsed;
        // Extraer roles del token
        const allRoles = this.getRoles(tokenParsed);
        const permissions = this.mapRolesToPermissions(allRoles);
        return permissions.includes(permission) || permissions.includes('all');
    }
    /**
     * Obtiene el estado actual de autenticación
     */
    getAuthState() {
        return {
            isAuthenticated: this.isAuthenticated(),
            isLoading: false,
            user: null,
            token: this.getToken(),
            refreshToken: this.getRefreshToken(),
            error: null,
        };
    }
    /**
     * Configura el refresh automático de tokens
     */
    setupTokenRefresh() {
        if (!this.keycloak)
            return;
        this.clearRefreshTimer();
        // Calcular tiempo hasta expiración
        const tokenParsed = this.keycloak.tokenParsed;
        if (!tokenParsed?.exp)
            return;
        const now = Math.floor(Date.now() / 1000);
        const expiresIn = tokenParsed.exp - now;
        const refreshIn = Math.max((expiresIn - keycloakTimeouts.minValiditySeconds) * 1000, keycloakTimeouts.checkInterval);
        this.refreshTimer = setTimeout(async () => {
            try {
                await this.refreshToken();
            }
            catch (error) {
                this.log('error', '❌ Error en refresh automático:', error);
            }
        }, refreshIn);
    }
    /**
     * Limpia el timer de refresh
     */
    clearRefreshTimer() {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
    }
    /**
     * Configura listeners de eventos de Keycloak
     */
    setupEventListeners() {
        if (!this.keycloak)
            return;
        this.keycloak.onTokenExpired = async () => {
            this.log('warn', '⚠️ Token expirado');
            const refreshed = await this.refreshToken();
            if (!refreshed) {
                await this.logout();
            }
        };
        this.keycloak.onAuthSuccess = () => {
            this.log('info', '✅ Autenticación exitosa');
        };
        this.keycloak.onAuthError = (error) => {
            this.log('error', '❌ Error de autenticación:', error);
        };
        this.keycloak.onAuthLogout = () => {
            this.log('info', '👋 Logout exitoso');
            this.clearRefreshTimer();
        };
    }
    /**
     * Mapea roles de Keycloak a permisos de la aplicación
     */
    mapRolesToPermissions(roles) {
        const permissions = new Set();
        roles.forEach(role => {
            const rolePermissions = roleMapping[role];
            if (rolePermissions) {
                rolePermissions.forEach(permission => permissions.add(permission));
            }
        });
        return Array.from(permissions);
    }
    /**
     * Función de logging condicional (solo errores críticos en producción)
     */
    log(level, message, ...args) {
        // En desarrollo, mostrar solo info, warn y error
        // En producción, mostrar solo errores
        const shouldLog = this.environment === 'development'
            ? ['info', 'warn', 'error'].includes(level)
            : level === 'error';
        if (shouldLog) {
            console[level](`[Keycloak] ${message}`, ...args);
        }
    }
    /**
     * Limpia recursos al destruir el servicio
     */
    destroy() {
        this.clearRefreshTimer();
    }
}
// Instancia singleton del servicio
export const keycloakService = new KeycloakService();
export default keycloakService;
